import { useEffect } from 'preact/hooks';
import { navigate, useRoute } from '../lib/router';
import { BookPage, BookPanel } from '../pages/BookFrame';
import { SectionPage } from '../pages/SectionPage';
import { ShelvesPage } from '../shelves/ShelvesPage';
import { QueuePage } from '../queue/QueuePage';
import { MyBooksPage } from '../mybooks/MyBooksPage';
import { PeoplePage } from '../discover/PeoplePages';
import { DiscoverPage } from '../discover/DiscoverPage';
import { useFlag } from './hooks';
import { FlagsPage } from '../pages/FlagsPage';
import { useIsAdmin } from '../lib/admin';
import { LaptopHeader, PhoneTopBar, SectionBar } from './Bars';
import { useWide } from './hooks';
import { NoticeProvider } from './notice';
import { HOME, bookIdFrom, peoplePageFrom, sectionFor, trailSection, type Section } from './sections';
import '../ui/ui.css';
import './shell.css';

// The signed-in app: header and section bar around whatever the address shows.
//   /shelves, /queue, /discover, /my-books, /insights   a section (/ is Shelves, home)
//   /book/<B-number>                                      the book page
//   /author/<id>, /series/<id>                            an author's or a series' page (Discover, Part 2)
//   /flags                                                feature flags (admin only)
// Anything else goes to Shelves.

export function Shell({ email, userId }: { email: string; userId: string }) {
  const { path, state } = useRoute();
  const wide = useWide();
  const isAdmin = useIsAdmin(userId);
  const flagsPage = path === '/flags';
  const bookId = bookIdFrom(path);
  const people = peoplePageFrom(path);
  const section = bookId || flagsPage || people ? undefined : sectionFor(path);

  useEffect(() => {
    if (!bookId && !section && !flagsPage && !people) navigate(HOME.path, { replace: true });
  }, [bookId, section, flagsPage, people?.id]);

  // On a shared page (a book, an author, a series), the bar highlights the section the trail began in.
  const current = section ?? trailSection(state);
  // On a laptop a book opens in the side panel over the section, or the author's or series' page, it came from.
  const fromPeople = state.from ? peoplePageFrom(state.from) : undefined;
  const panel = Boolean(bookId && wide && state.from && !state.full && (sectionFor(state.from) || fromPeople));
  const underneath = panel ? (fromPeople ? undefined : sectionFor(state.from!)) : section;
  const peopleShown = panel ? fromPeople : people;

  // The top bar's height, for things pinned just under it (Shelves' tabs and filters, Kristi Oct 28).
  useEffect(() => {
    const bar = document.querySelector<HTMLElement>('.hdr, .ptop');
    const root = document.documentElement;
    if (!bar) {
      root.style.setProperty('--top-bar-h', '0px');
      return;
    }
    const set = () => root.style.setProperty('--top-bar-h', `${bar.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(bar);
    return () => ro?.disconnect();
  }, [wide, bookId]);

  // The phone's section bar's height, for things that sit just above it (the Queue's ↑ Top and
  // Select several bar; an iPhone's home bar makes it taller).
  useEffect(() => {
    const root = document.documentElement;
    const bar = wide ? null : document.querySelector<HTMLElement>('.sbar');
    if (!bar) {
      root.style.setProperty('--section-bar-h', '0px');
      return;
    }
    const set = () => root.style.setProperty('--section-bar-h', `${bar.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(bar);
    return () => ro?.disconnect();
  }, [wide, bookId]);

  return (
    <NoticeProvider>
      <div class={wide ? 'shell wide' : 'shell narrow'}>
        {wide ? (
          <LaptopHeader current={current} email={email} isAdmin={isAdmin} />
        ) : bookId || people ? null : (
          <PhoneTopBar title={flagsPage ? 'Feature flags' : current === HOME ? 'Reading List' : current.name} email={email} isAdmin={isAdmin} />
        )}
        <main id="main">
          {underneath ? (
            <SectionView section={underneath} wide={wide} />
          ) : peopleShown ? (
            <PeoplePage kind={peopleShown.kind} id={peopleShown.id} state={state} wide={wide} under={panel} />
          ) : bookId ? (
            <BookPage id={bookId} state={state} wide={wide} />
          ) : flagsPage ? (
            <FlagsPage userId={userId} isAdmin={isAdmin} />
          ) : null}
        </main>
        {panel && bookId ? <BookPanel id={bookId} state={state} /> : null}
        {!wide ? <SectionBar current={current} /> : null}
      </div>
    </NoticeProvider>
  );
}

/** A section: Shelves (Phase 6.2), the Queue (6.3), My books (6.4) and Discover (7) once their flags are on for you, the others' placeholders until built. */
function SectionView({ section, wide }: { section: Section; wide: boolean }) {
  const shelvesOn = useFlag('shelves');
  const queueOn = useFlag('queue');
  const myBooksOn = useFlag('my_books');
  const discoverOn = useFlag('discover');
  // While the flag is being asked, show nothing rather than flash the placeholder (Kristi, Oct 29).
  if (section.key === 'shelves' && shelvesOn === null) return <div class="page" aria-busy="true" />;
  if (section.key === 'shelves' && shelvesOn) return <ShelvesPage wide={wide} />;
  if (section.key === 'queue' && queueOn === null) return <div class="page" aria-busy="true" />;
  if (section.key === 'queue' && queueOn) return <QueuePage wide={wide} />;
  if (section.key === 'my-books' && myBooksOn === null) return <div class="page" aria-busy="true" />;
  if (section.key === 'my-books' && myBooksOn) return <MyBooksPage wide={wide} />;
  if (section.key === 'discover' && discoverOn === null) return <div class="page" aria-busy="true" />;
  if (section.key === 'discover' && discoverOn) return <DiscoverPage wide={wide} />;
  return <SectionPage section={section} wide={wide} />;
}
